import { motion } from 'motion/react';
import { useCallback, useState } from 'react';
import { RiGroup2Line } from 'react-icons/ri';
import { Separator } from '@/components/primitives/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/primitives/tabs';
import { SubscriberCredentials } from '@/components/subscribers/credentials/subscriber-credentials';
import { Preferences } from '@/components/subscribers/preferences/preferences';
import { PreferencesSkeleton } from '@/components/subscribers/preferences/preferences-skeleton';
import { SubscriberActivity } from '@/components/subscribers/subscriber-activity';
import { SubscriberOverviewForm } from '@/components/subscribers/subscriber-overview-form';
import { SubscriberOverviewSkeleton } from '@/components/subscribers/subscriber-overview-skeleton';
import { SubscriberSubscriptions } from '@/components/subscribers/subscriptions/subscriber-subscriptions';
import TruncatedText from '@/components/truncated-text';
import { useFetchSubscriber } from '@/hooks/use-fetch-subscriber';
import useFetchSubscriberPreferences from '@/hooks/use-fetch-subscriber-preferences';

type OverviewFocusField = 'email' | 'phone';

type SubscriberOverviewProps = {
  subscriberId: string;
  readOnly?: boolean;
  onCloseDrawer?: () => void;
  closeOnSave?: boolean;
  focusField?: OverviewFocusField;
  onFocusHandled?: () => void;
};

const SubscriberOverview = (props: SubscriberOverviewProps) => {
  const { subscriberId, readOnly = false, onCloseDrawer, closeOnSave = false, focusField, onFocusHandled } = props;
  const { data, isPending } = useFetchSubscriber({
    subscriberId,
  });

  if (isPending) {
    return <SubscriberOverviewSkeleton />;
  }

  return (
    <SubscriberOverviewForm
      subscriber={data!}
      readOnly={readOnly}
      onCloseDrawer={onCloseDrawer}
      closeOnSave={closeOnSave}
      focusField={focusField}
      onFocusHandled={onFocusHandled}
    />
  );
};

type SubscriberPreferencesProps = {
  subscriberId: string;
  readOnly?: boolean;
};

const SubscriberPreferences = (props: SubscriberPreferencesProps) => {
  const { subscriberId, readOnly = false } = props;
  const [selectedContextKeys, setSelectedContextKeys] = useState<string[] | undefined>(['']);

  const { data, isPending } = useFetchSubscriberPreferences({
    subscriberId,
    contextKeys: selectedContextKeys,
  });

  if (isPending) {
    return <PreferencesSkeleton />;
  }

  return (
    <Preferences
      subscriberPreferences={data!}
      subscriberId={subscriberId}
      readOnly={readOnly}
      contextKeys={selectedContextKeys}
      onContextChange={setSelectedContextKeys}
    />
  );
};

const tabTriggerClasses =
  'hover:data-[state=inactive]:text-foreground-950 py-3 rounded-none [&>span]:h-5 px-0 relative';

type SubscriberTabsProps = {
  subscriberId: string;
  readOnly?: boolean;
  onCloseDrawer?: () => void;
  closeOnSave?: boolean;
};

export function SubscriberTabs(props: SubscriberTabsProps) {
  const { subscriberId, readOnly = false, onCloseDrawer, closeOnSave = false } = props;
  const [tab, setTab] = useState('overview');
  const [overviewFocusField, setOverviewFocusField] = useState<OverviewFocusField | undefined>();

  const focusOverviewField = useCallback((field: OverviewFocusField) => {
    setOverviewFocusField(field);
    setTab('overview');
  }, []);

  const clearOverviewFocusField = useCallback(() => setOverviewFocusField(undefined), []);

  return (
    <Tabs className="flex h-full w-full flex-col" value={tab} onValueChange={setTab}>
      <header className="border-bg-soft flex h-12 w-full flex-row items-center gap-3 border-b px-3 py-4">
        <div className="flex flex-1 items-center gap-1 overflow-hidden text-sm font-medium">
          <RiGroup2Line className="size-5 p-0.5" />
          <TruncatedText className="flex-1">Subscriber Profile - {subscriberId}</TruncatedText>
        </div>
      </header>

      <TabsList className="border-bg-soft h-auto w-full items-center gap-6 rounded-none border-b bg-transparent px-3 py-0">
        <TabsTrigger value="overview" className={tabTriggerClasses} variant="regular" size="lg">
          <span>Overview</span>
          {tab === 'overview' && <ActiveTabIndicator />}
        </TabsTrigger>
        <TabsTrigger value="credentials" className={tabTriggerClasses} variant="regular" size="lg">
          <span>Credentials</span>
          {tab === 'credentials' && <ActiveTabIndicator />}
        </TabsTrigger>
        <TabsTrigger value="preferences" className={tabTriggerClasses} variant="regular" size="lg">
          <span>Preferences</span>
          {tab === 'preferences' && <ActiveTabIndicator />}
        </TabsTrigger>
        <TabsTrigger value="subscriptions" className={tabTriggerClasses} variant="regular" size="lg">
          <span>Subscriptions</span>
          {tab === 'subscriptions' && <ActiveTabIndicator />}
        </TabsTrigger>
        <TabsTrigger value="activity-feed" className={tabTriggerClasses} variant="regular" size="lg">
          <span>Activity Feed</span>
          {tab === 'activity-feed' && <ActiveTabIndicator />}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="overview" className="h-full w-full overflow-y-auto">
        <SubscriberOverview
          subscriberId={subscriberId}
          readOnly={readOnly}
          onCloseDrawer={onCloseDrawer}
          closeOnSave={closeOnSave}
          focusField={overviewFocusField}
          onFocusHandled={clearOverviewFocusField}
        />
      </TabsContent>
      <TabsContent value="credentials" className="h-full w-full overflow-y-auto">
        <SubscriberCredentials subscriberId={subscriberId} readOnly={readOnly} onEditInOverview={focusOverviewField} />
      </TabsContent>
      <TabsContent value="preferences" className="h-full w-full overflow-y-auto">
        <SubscriberPreferences subscriberId={subscriberId} readOnly={readOnly} />
      </TabsContent>
      <TabsContent value="subscriptions" className="h-full w-full overflow-y-auto">
        <SubscriberSubscriptions subscriberId={subscriberId} />
      </TabsContent>
      <TabsContent value="activity-feed" className="h-full w-full overflow-y-auto">
        <SubscriberActivity subscriberId={subscriberId} />
      </TabsContent>
      <Separator />
    </Tabs>
  );
}

const ActiveTabIndicator = () => {
  return <motion.div layoutId="active-tab" className="bg-primary-base absolute bottom-0 left-0 right-0 z-10 h-[2px]" />;
};
